/**
 * Globe rendering using Canvas 2D API (faster than WebGL for many polygons)
 * Phase 6: Dual Rendering - Globe View (Canvas 2D)
 *
 * Canvas 2D is more efficient than PixiJS Graphics for rendering hundreds of filled polygons
 */

import { flattenSphere, returnArrayToPool } from '../core/projections.js';

/**
 * Create Canvas 2D element for globe rendering
 * @param {number} size - Canvas size (width and height)
 * @param {number} x - X position
 * @param {number} y - Y position
 * @returns {Object} { canvas, ctx }
 */
export function createGlobeCanvas(size, x, y) {
    const canvas = document.createElement('canvas');
    canvas.width = size;
    canvas.height = size;
    canvas.style.position = 'absolute';
    canvas.style.left = `${x}px`;
    canvas.style.top = `${y}px`;
    canvas.style.pointerEvents = 'none'; // Let PixiJS canvas handle mouse events

    const ctx = canvas.getContext('2d');

    return { canvas, ctx };
}

/**
 * Render all countries to globe using Canvas 2D
 * @param {CanvasRenderingContext2D} ctx - Canvas 2D context
 * @param {number} radius - Globe radius
 * @param {Map} countries - Country data
 * @param {Map} rotationCache - Pre-computed rotated 3D coordinates
 */
export function renderGlobeCanvas2D(ctx, radius, countries, rotationCache) {
    const size = radius * 2;

    // Clear canvas
    ctx.clearRect(0, 0, size, size);

    // Draw ocean circle background
    ctx.fillStyle = '#1a1a2e';
    ctx.beginPath();
    ctx.arc(radius, radius, radius, 0, Math.PI * 2);
    ctx.fill();

    // Render all countries
    for (const [countryId, country] of countries) {
        const rotatedPaths = rotationCache.get(countryId);

        for (let i = 0; i < country.paths.length; i++) {
            const rotated3D = rotatedPaths[i];

            // Hemisphere culling: check if any vertices are front-facing
            let hasFrontFacing = false;
            for (let j = 0; j < rotated3D.length; j += 3) {
                if (rotated3D[j] > 0) {
                    hasFrontFacing = true;
                    break;
                }
            }

            if (!hasFrontFacing) continue;

            // Flatten to 2D circle
            const circleCoords = flattenSphere(rotated3D, radius);

            // Draw polygon using Canvas 2D
            if (circleCoords.length >= 4) {
                ctx.fillStyle = `rgb(${country.color[0]}, ${country.color[1]}, ${country.color[2]})`;
                ctx.beginPath();
                ctx.moveTo(circleCoords[0], circleCoords[1]);

                for (let k = 2; k < circleCoords.length; k += 2) {
                    ctx.lineTo(circleCoords[k], circleCoords[k + 1]);
                }

                ctx.closePath();
                ctx.fill();
            }

            // Return array to pool
            returnArrayToPool(circleCoords);
        }
    }
}
